import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTryOnCredits } from "@/context/TryOnCreditsContext";
import { Seo } from "@/components/Seo";
import { CheckoutLightbox } from "@/components/CheckoutLightbox";
import { SchoolVerifyModal } from "@/components/SchoolVerifyModal";
import { Sparkles, Check, ShieldCheck, Wand2, Camera, Infinity as InfinityIcon, ArrowRight } from "lucide-react";

/**
 * /try-on/pricing — dedicated tier-select page for AI Try-On credits.
 * Source of truth for tiers is the backend `GET /api/tryon/tiers`.
 */
export default function TryOnPricing() {
  const { user } = useAuth();
  const globalCredits = useTryOnCredits();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [credits, setCredits] = useState(null);
  const [pack, setPack] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);

  useEffect(() => {
    api.tryOnTiers().then(({ data }) => setTiers(data.tiers || [])).catch(() => {});
    if (user) api.tryOnCredits().then(({ data }) => setCredits(data)).catch(() => {});
  }, [user]);

  const buy = (p) => {
    if (!user) {
      toast("Sign in first — we'll link the credits to your account.");
      navigate("/auth?next=/try-on/pricing");
      return;
    }
    setPack(p);
  };

  const active = tiers.find((t) => t.pack === pack) || null;

  return (
    <div data-testid="tryon-pricing-page" className="min-h-screen bg-ivory">
      <Seo
        title="AI Try-On Credits"
        description="See yourself in any dress before you buy. Pick a Heather's AI Try-On pack — 1, 3, 10 or 15 photorealistic try-ons."
        path="/try-on/pricing"
      />

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-cream">
        <div className="absolute inset-0 bg-gradient-to-br from-blush/60 via-ivory to-champagne/30 opacity-90" aria-hidden />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 py-20 sm:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="font-label text-[10px] tracking-[0.3em] text-rosemetal mb-3 flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> AI TRY-ON · POWERED BY HEATHER
            </p>
            <h1 className="font-display text-5xl sm:text-6xl leading-[1.02] text-ink mb-6">
              See yourself in the dress <span className="italic text-rosemetal">before</span> you buy it.
            </h1>
            <p className="font-body text-lg text-ink/70 max-w-xl leading-relaxed mb-6">
              Upload a selfie. Heather's AI styles you in any dress in the boutique — real face, real light,
              real proportion. No returns anxiety. No more guessing.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 font-label text-[10px] tracking-widest text-ink/60">
              <span className="flex items-center gap-1.5"><Camera className="w-3.5 h-3.5 text-rosemetal" /> HD PORTRAITS</span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-rosemetal" /> PRIVATE · YOURS FOREVER</span>
              <span className="flex items-center gap-1.5"><InfinityIcon className="w-3.5 h-3.5 text-rosemetal" /> NO EXPIRY</span>
            </div>
          </div>
          <div className="relative hidden lg:block">
            <div className="aspect-[4/5] rounded-sm overflow-hidden bg-cream shadow-[0_40px_80px_-30px_rgba(10,10,10,0.35)]">
              <img
                src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?fm=jpg&q=85&w=800"
                alt="AI try-on preview"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-6 -left-8 bg-ink text-cream rounded-sm p-4 shadow-xl max-w-[220px]">
              <p className="font-label text-[9px] tracking-widest text-champagne mb-1">RESULT · 18 SECONDS</p>
              <p className="font-display text-lg leading-tight">"I actually cried it looked so real."</p>
              <p className="font-label text-[9px] text-ivory/60 mt-1">— MADISON, KETTLE MORAINE HS</p>
            </div>
          </div>
        </div>
      </div>

      {/* Current balance */}
      {user && credits && (
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-8">
          <div
            data-testid="tryon-balance"
            className="inline-flex items-center gap-3 border border-cream rounded-sm bg-cream/40 px-4 py-2.5"
          >
            <Wand2 className="w-4 h-4 text-rosemetal" />
            <span className="font-label text-[10px] tracking-widest text-ink">
              YOUR BALANCE ·{" "}
              <b className="text-rosemetal">
                {credits.is_vip ? "UNLIMITED (VIP)" : `${credits.credits} TRY-ON${credits.credits === 1 ? "" : "S"}`}
              </b>
            </span>
          </div>
        </div>
      )}

      {/* Pricing cards */}
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <div className="text-center mb-14">
          <p className="font-label text-[10px] tracking-[0.3em] text-rosemetal mb-3">PICK YOUR PACK</p>
          <h2 className="font-display text-4xl sm:text-5xl text-ink">Three ways to try on.</h2>
          <p className="font-body text-base text-ink/60 mt-4 max-w-xl mx-auto">
            One-time. No auto-renew. No hidden fees. Credits never expire.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 lg:gap-7" data-testid="pricing-tiers">
          {tiers.map((t, i) => (
            <motion.div
              key={t.pack}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className={`relative rounded-sm border ${
                t.highlight
                  ? "border-rosemetal shadow-[0_30px_60px_-25px_rgba(183,110,121,0.5)] md:-mt-4 md:mb-4"
                  : "border-cream"
              } bg-ivory p-8 flex flex-col`}
              data-testid={`pricing-tier-${t.pack}`}
            >
              {t.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-ink text-champagne font-label text-[9px] tracking-[0.25em] px-3 py-1.5 rounded-full shadow">
                  {t.badge}
                </div>
              )}
              <div className="mb-6">
                <p className="font-label text-[10px] tracking-[0.3em] text-rosemetal mb-2">
                  {t.credits} {t.credits === 1 ? "IMAGE" : "IMAGES"}
                </p>
                <h3 className="font-display text-3xl text-ink leading-tight mb-3">{t.name}</h3>
                <div className="flex items-baseline gap-1.5">
                  <span className="font-display text-5xl text-ink">${t.price.toFixed(2)}</span>
                  <span className="font-body text-sm text-ink/50">one-time</span>
                </div>
                <p className="font-label text-[9px] text-ink/50 tracking-widest mt-1">
                  {t.credits === 1 ? "1 image" : `~$${t.per_image.toFixed(2)} per image`}
                </p>
              </div>

              <ul className="space-y-2.5 mb-8 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 font-body text-sm text-ink/80">
                    <Check className="w-4 h-4 text-rosemetal shrink-0 mt-0.5" strokeWidth={2.25} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                data-testid={`pricing-tier-cta-${t.pack}`}
                onClick={() => buy(t.pack)}
                className={`w-full py-4 font-label text-[11px] tracking-[0.25em] rounded-sm transition-colors flex items-center justify-center gap-2 ${
                  t.highlight
                    ? "bg-rosemetal text-cream hover:bg-ink"
                    : "bg-ink text-cream hover:bg-rosemetal"
                }`}
              >
                {(t.cta || "Get pack").toUpperCase()} <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Reassurance strip */}
        <div className="mt-16 grid sm:grid-cols-3 gap-6 border-t border-cream pt-10">
          <Reassure icon={ShieldCheck} title="Encrypted payment" body="Card details handled by Stripe. We never see or store them." />
          <Reassure icon={InfinityIcon} title="Credits never expire" body="Use them this prom, next homecoming, or graduation. Yours forever." />
          <Reassure icon={Wand2} title="Real Heather touch" body="Not just an algorithm — Heather personally reviews your account any time you need." />
        </div>

        {/* FAQ */}
        <div className="mt-20 max-w-2xl mx-auto">
          <p className="font-label text-[10px] tracking-[0.3em] text-rosemetal mb-3 text-center">FAQ</p>
          <h2 className="font-display text-3xl sm:text-4xl text-center text-ink mb-8">
            The questions you'd ask Heather.
          </h2>
          <div className="space-y-4">
            <Faq q="What am I actually paying for?" a="AI generation compute + our team's stylist review time. Each try-on runs through Heather's tuned AI model and produces an HD portrait of you in the dress you picked." />
            <Faq q="Do credits expire?" a="No. Ever. Use them across prom, homecoming, graduation — even next year's season." />
            <Faq q="Is my selfie private?" a="Yes. It's stored encrypted, only visible to you, and can be deleted from your account at any time." />
            <Faq q="Can I get a refund?" a="Unused credits are refundable within 14 days. Once a try-on is generated, that credit is spent — we've already paid the compute cost." />
          </div>
        </div>
      </div>

      <CheckoutLightbox
        open={!!active}
        onClose={() => setPack(null)}
        onSchoolVerifyRequired={() => { setPack(null); setVerifyOpen(true); }}
        onSuccess={() => {
          api.tryOnCredits().then(({ data }) => setCredits(data)).catch(() => {});
          globalCredits.refresh();
        }}
        onDone={() => {
          setPack(null);
          // If the user was mid-flow on a specific dress, send them straight back.
          try {
            const last = localStorage.getItem("hb_last_tryon_product_url");
            if (last) { localStorage.removeItem("hb_last_tryon_product_url"); navigate(last); return; }
          } catch { /* ignore */ }
        }}
        kind="credits"
        payload={{ pack }}
        title={active ? active.name : "Try-On credits"}
        lines={active ? [{ name: `${active.credits} AI try-on credit${active.credits === 1 ? "" : "s"}`, price: `$${active.price.toFixed(2)}` }] : []}
        total={active ? active.price : 0}
        note="Credits never expire — style yourself in any dress, any time."
        cta="Continue to Secure Payment"
      />

      <SchoolVerifyModal
        open={verifyOpen}
        reason="tryon"
        onClose={() => { setVerifyOpen(false); globalCredits.refresh(); }}
      />
    </div>
  );
}

function Reassure({ icon: Icon, title, body }) {
  return (
    <div>
      <Icon className="w-5 h-5 text-rosemetal mb-3" strokeWidth={1.75} />
      <p className="font-display text-xl text-ink leading-tight mb-1.5">{title}</p>
      <p className="font-body text-sm text-ink/60 leading-relaxed">{body}</p>
    </div>
  );
}

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-cream rounded-sm">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`faq-toggle-${q.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
        className="w-full text-left px-5 py-4 flex items-center justify-between font-body text-base text-ink hover:bg-cream/30 transition-colors"
      >
        <span>{q}</span>
        <span className={`font-label text-xl text-rosemetal transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      {open && (
        <div className="px-5 pb-5 font-body text-sm text-ink/70 leading-relaxed">{a}</div>
      )}
    </div>
  );
}
